import React, { useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import { Typography } from '@paintscout/ui'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import { useLocation } from 'react-router-dom'
import pageMargins from '../styles/snippets/pageMargins'
import { CloseButton } from '@paintscout/react-quote'

const useStyles = makeStyles<Theme, MultiTabCheckerProps>(
  (theme) => ({
    root: {
      minWidth: '100%',
      display: 'block',
      backgroundColor: theme.palette.error.light
    },
    alert: {
      ...pageMargins(theme, ''),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      color: theme.palette.error.dark,
      padding: `${theme.spacing(1.5)}px 0`
    },
    alertContent: {
      display: 'flex',
      alignItems: 'center'
    },
    icon: {
      marginRight: theme.spacing(1)
    },
    closeIcon: {
      marginRight: theme.spacing(0.5)
    }
  }),
  { name: 'MultiTabChecker' }
)

export interface MultiTabCheckerProps {
  classes?: StyleClasses<typeof useStyles>
}

function MultiTabChecker({ ...props }: MultiTabCheckerProps) {
  const classes = useStyles(props)
  const [isMultipleTabsOpen, setIsMultipleTabsOpen] = useState(false)
  const location = useLocation()
  const isInSettings = location.pathname.includes('/settings')
  const tabId = Math.random().toString(36).substring(7)
  const [isClosed, setIsClosed] = useState(false)

  useEffect(() => {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel('tab')
      let lastResponseTime = Date.now()
      let pollingInterval: NodeJS.Timeout | null = null
      let listeningInterval: NodeJS.Timeout | null = null
      let responseTimeout: NodeJS.Timeout | null = null

      // Polling to inform other tabs of our presence in settings
      const startPolling = () => {
        if (pollingInterval) clearInterval(pollingInterval)
        pollingInterval = setInterval(() => {
          channel.postMessage(`another-tab-${tabId}`)
        }, 5000)
      }

      // Timeout to check if other tabs are still open
      const startTimeout = () => {
        if (listeningInterval) clearInterval(listeningInterval)
        if (responseTimeout) clearTimeout(responseTimeout)

        responseTimeout = setTimeout(() => {
          const timeSinceLastResponse = Date.now() - lastResponseTime
          if (timeSinceLastResponse > 10000) {
            setIsMultipleTabsOpen(false)
            if (listeningInterval) clearInterval(listeningInterval)
            if (pollingInterval) clearInterval(pollingInterval)
            responseTimeout = null
            pollingInterval = null
            listeningInterval = null
          }
        }, 10000)
      }

      // Handle messages from other tabs, ignore your own
      const handleMessage = (msg: MessageEvent) => {
        if (msg.data.includes('another-tab') && msg.data !== `${tabId}`) {
          setIsMultipleTabsOpen(true)
          lastResponseTime = Date.now()

          if (!pollingInterval && isInSettings) {
            startPolling()
          }
          if (!listeningInterval && isInSettings) {
            startTimeout()
          }
        }
      }

      // Start listening, polling and timeout if in settings
      if (isInSettings) {
        channel.addEventListener('message', handleMessage)
        startTimeout()
        startPolling()
        setIsClosed(false)
      } else {
        // cleanup if not in settings
        if (pollingInterval) clearInterval(pollingInterval)
        if (responseTimeout) clearTimeout(responseTimeout)
        if (listeningInterval) clearInterval(listeningInterval)
        channel.removeEventListener('message', handleMessage)
      }

      // Cleanup
      return () => {
        if (pollingInterval) clearInterval(pollingInterval)
        if (responseTimeout) clearTimeout(responseTimeout)
        if (listeningInterval) clearInterval(listeningInterval)
        channel.removeEventListener('message', handleMessage)
      }
    }
  }, [isInSettings])

  return (
    <div className={classes.root}>
      {isMultipleTabsOpen && isInSettings && !isClosed && (
        <div className={classes.alert}>
          <div className={classes.alertContent}>
            <ErrorOutlineIcon className={classes.icon} />
            <Typography variant="body1">
              PaintScout is open in multiple tabs. To prevent conflicts or lost changes, please close any extra tabs and
              continue in one.
            </Typography>
          </div>
          <div>
            <CloseButton className={classes.closeIcon} onCancel={() => setIsClosed(true)} disabled={isClosed} />
          </div>
        </div>
      )}
    </div>
  )
}

export default MultiTabChecker
